class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = this.querySelector('#cart-notification');
    this.itemsContainer = this.querySelector('[data-md-cart-items]');
    this.emptyState = this.querySelector('[data-md-cart-empty]');
    this.footer = this.querySelector('[data-md-cart-footer]');
    this.upsellSection = this.querySelector('[data-md-upsell-section]');
    this.goodieCard = this.querySelector('[data-md-goodie-card]');
    this.vipCard = this.querySelector('[data-md-vip-card]');
    this.country = (this.dataset.country || 'DE').toUpperCase();
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.cartJsonUrl = this.dataset.cartJsonUrl || '/cart.js';
    this.cartChangeUrl = this.dataset.cartChangeUrl || '/cart/change';
    this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
    this.activeElement = null;
    this.lastCart = null;

    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);

    this.addEventListener('click', this.handleDrawerClick.bind(this));
    document.addEventListener('click', this.onDocumentClick, true);
    this.notification.addEventListener('keyup', this.onKeyUp);

    this.refresh({ silent: true });
  }

  open() {
    this.classList.add('is-active');
    this.notification.setAttribute('aria-hidden', 'false');
    document.body.classList.add('md-cart-open');
    this.refresh();

    window.setTimeout(() => {
      const closeButton = this.querySelector('.md-cart-close');
      if (closeButton) closeButton.focus();
      if (typeof window.trapFocus === 'function') window.trapFocus(this.notification);
    }, 290);
  }

  close() {
    this.classList.remove('is-active');
    this.notification.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('md-cart-open');

    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.activeElement);
    if (this.activeElement && typeof this.activeElement.focus === 'function') this.activeElement.focus();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  handleKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }

  handleDocumentClick(event) {
    const link = event.target.closest('a[href]');
    if (!link || link.closest('cart-notification')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;

    let linkPath;
    let cartPath;
    try {
      linkPath = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
      cartPath = new URL(this.cartUrl, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return;
    }

    if (linkPath !== cartPath) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.setActiveElement(link);
    this.open();
  }

  handleDrawerClick(event) {
    const closeButton = event.target.closest('[data-md-cart-close]');
    if (closeButton) {
      event.preventDefault();
      this.close();
      return;
    }

    const quantityButton = event.target.closest('[data-md-quantity]');
    if (quantityButton) {
      event.preventDefault();
      const quantity = Number(quantityButton.dataset.quantity || 0);
      this.changeLine(quantityButton.dataset.key, quantity);
      return;
    }

    const removeButton = event.target.closest('[data-md-remove]');
    if (removeButton) {
      event.preventDefault();
      this.changeLine(removeButton.dataset.key, 0);
      return;
    }

    const upsellButton = event.target.closest('[data-md-upsell-add]');
    if (upsellButton) {
      event.preventDefault();
      this.addUpsell(upsellButton);
      return;
    }

  }

  async refresh(options = {}) {
    try {
      if (!options.silent) this.notification.setAttribute('aria-busy', 'true');
      const response = await fetch(this.cartJsonUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Cart could not be loaded');
      const cart = await response.json();
      this.renderCart(cart);
      return cart;
    } catch (error) {
      if (!options.silent) this.showError('Der Warenkorb konnte gerade nicht aktualisiert werden. Bitte versuche es erneut.');
      return null;
    } finally {
      this.notification.removeAttribute('aria-busy');
    }
  }

  renderCart(cart) {
    this.lastCart = cart;
    const hasItems = cart.item_count > 0;

    this.itemsContainer.innerHTML = cart.items.map((item) => this.renderLineItem(item)).join('');
    this.emptyState.hidden = hasItems;
    this.footer.hidden = !hasItems;
    this.querySelector('[data-md-cart-count]').textContent = String(cart.item_count);
    this.querySelector('[data-md-cart-subtotal]').textContent = this.formatMoney(cart.total_price, cart.currency);

    this.renderProgress(cart.total_price, cart.currency);
    this.syncGoodieUnlock(cart);
    this.syncVipUnlock(cart);
    this.syncRecommendations(cart);
    this.updateHeaderCount(cart.item_count);
  }

  renderLineItem(item) {
    const image = item.image
      ? '<img src="' + this.escapeAttribute(item.image) + '" alt="" width="184" height="198" loading="lazy">'
      : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? '<p class="md-cart-item__meta">' + this.escapeHTML(item.variant_title) + '</p>'
      : '<p class="md-cart-item__meta">Duftwachs</p>';
    const sellingPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? '<p class="md-cart-item__meta">' + this.escapeHTML(item.selling_plan_allocation.selling_plan.name) + '</p>'
      : '';
    const calendarNote = item.handle === 'duftwachs-adventskalender-2026'
      ? '<p class="md-cart-calendar-note"><strong>Separater Adventskalender-Versand:</strong> ' +
        (this.country === 'AT' ? '9,90 € nach Österreich' : '5,99 € nach Deutschland') +
        ' · Versand 01.–20.11.2026. Andere sofort lieferbare Artikel werden getrennt verschickt.</p>'
      : '';
    const itemUrl = item.url || '#';
    const key = this.escapeAttribute(item.key);
    const quantity = Number(item.quantity || 0);

    return '<article class="md-cart-item">' +
      '<a class="md-cart-item__image" href="' + this.escapeAttribute(itemUrl) + '">' + image + '</a>' +
      '<div class="md-cart-item__content">' +
        '<a class="md-cart-item__title" href="' + this.escapeAttribute(itemUrl) + '">' + this.escapeHTML(item.product_title) + '</a>' +
        variant + sellingPlan +
        '<div class="md-cart-item__bottom">' +
          '<div class="md-cart-quantity" aria-label="Menge für ' + this.escapeAttribute(item.product_title) + '">' +
            '<button type="button" data-md-quantity data-key="' + key + '" data-quantity="' + Math.max(0, quantity - 1) + '" aria-label="Menge verringern">−</button>' +
            '<span aria-live="polite">' + quantity + '</span>' +
            '<button type="button" data-md-quantity data-key="' + key + '" data-quantity="' + (quantity + 1) + '" aria-label="Menge erhöhen">＋</button>' +
          '</div>' +
          '<strong class="md-cart-item__price">' + this.formatMoney(item.final_line_price, this.lastCart && this.lastCart.currency) + '</strong>' +
        '</div>' +
        '<button class="md-cart-remove" type="button" data-md-remove data-key="' + key + '">Entfernen</button>' +
      '</div>' +
      calendarNote +
    '</article>';
  }

  renderProgress(totalPrice, currency) {
    const goodieThreshold = 4900;
    const shippingThreshold = this.country === 'AT' ? 8900 : 6900;
    const vipThreshold = 9900;
    const percent = Math.max(0, Math.min(100, (totalPrice / vipThreshold) * 100));
    const destination = this.country === 'AT' ? 'Österreich' : 'Deutschland';
    let copy;

    if (totalPrice < goodieThreshold) {
      copy = 'Noch <strong>' + this.formatMoney(goodieThreshold - totalPrice, currency) + '</strong> bis zu deiner kostenlosen Duftprobe.';
    } else if (totalPrice < shippingThreshold) {
      copy = '<strong>Duftprobe freigeschaltet!</strong> Noch ' + this.formatMoney(shippingThreshold - totalPrice, currency) + ' bis zum kostenlosen Versand nach ' + destination + '.';
    } else if (totalPrice < vipThreshold) {
      copy = '<strong>Duftprobe &amp; Versand gesichert!</strong> Noch ' + this.formatMoney(vipThreshold - totalPrice, currency) + ' bis zum exklusiven VIP Mini.';
    } else {
      copy = '<strong>VIP Level erreicht!</strong> Alle Meilensteine sind freigeschaltet.';
    }

    this.querySelector('[data-md-progress-copy]').innerHTML = copy;
    this.querySelector('[data-md-progress-fill]').style.width = percent + '%';
    this.querySelector('[data-md-progress-truck]').style.left = percent + '%';
    this.querySelector('[data-md-shipping-threshold]').textContent = this.country === 'AT' ? '89 €' : '69 €';

    const thresholds = {
      goodie: goodieThreshold,
      shipping: shippingThreshold,
      vip: vipThreshold,
    };
    const nextMilestone = totalPrice < goodieThreshold
      ? 'goodie'
      : totalPrice < shippingThreshold
        ? 'shipping'
        : totalPrice < vipThreshold
          ? 'vip'
          : null;

    this.querySelectorAll('[data-md-milestone]').forEach((milestone) => {
      const key = milestone.dataset.mdMilestone;
      const isComplete = totalPrice >= thresholds[key];
      const isNext = key === nextMilestone;
      milestone.classList.toggle('is-complete', isComplete);
      milestone.classList.toggle('is-next', isNext);
      if (isNext) milestone.setAttribute('aria-current', 'step');
      else milestone.removeAttribute('aria-current');
    });
  }

  syncGoodieUnlock(cart) {
    if (!this.goodieCard) return;

    const goodieThreshold = 4900;
    this.goodieCard.hidden = cart.item_count === 0 || cart.total_price < goodieThreshold;
  }

  syncVipUnlock(cart) {
    if (!this.vipCard) return;

    const vipThreshold = 9900;
    const vipProductId = String(this.vipCard.dataset.productId || '');
    const alreadyInCart = cart.items.some((item) => String(item.product_id) === vipProductId);
    this.vipCard.hidden = cart.item_count === 0 || cart.total_price < vipThreshold || alreadyInCart;
  }

  itemSignature(item) {
    return [
      item && item.product_title,
      item && item.title,
      item && item.variant_title,
      item && item.handle,
      item && item.product_type,
      item && item.vendor,
      item && item.product_description,
    ].filter(Boolean).join(' ').toLowerCase();
  }

  isHardwareItem(item) {
    const signature = this.itemSignature(item);
    return /duftlampe|stövchen|stoevchen|wax warmer|oil burner|fragrance warmer/.test(signature);
  }

  isWaxItem(item) {
    if (this.isHardwareItem(item)) return false;

    const signature = this.itemSignature(item);
    const excluded = /adventskalender|preorder|vorbestellung|monthly|mystery box|vip mini|geschenk|duftkerze|zubehör|zubehor|ersatz|glühbirne|gluhbirne/.test(signature);
    if (excluded) return false;

    const waxTerms = /duftwachs|snapbar|snaps|mëlt bubble|melt bubble|squeezemëlt|squeezemelt|wax melt|aromeffex|starter set|icons/.test(signature);
    const meltVendor = signature.includes('mëltdown') || signature.includes('meltdown');
    return waxTerms || meltVendor;
  }

  normalizeSignal(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  inferScentFamilies(value) {
    const signature = this.normalizeSignal(value);
    const families = new Set();
    const familyTerms = {
      citrus: /zitrone|lemon|limette|lime|citrus|bergamott|grapefruit|orange|mandarin|yuzu/,
      fruity: /beere|berry|pfirsich|peach|melone|melon|ananas|pineapple|mango|apfel|apple|kirsche|cherry|erdbeer|strawberry|passion|frucht|fruity|kokos|coconut/,
      tropical: /tropisch|tropical|ananas|pineapple|mango|passion|kokos|coconut|morocco|marokko/,
      gourmand: /kaffee|coffee|waffle|karamell|caramel|vanille|vanilla|marsh|bakery|back|cake|kuchen|cookie|brunch|bun|latte|schokolade|chocolate|zimt|cinnamon|pumpkin spice/,
      floral: /jasmin|jasmine|rose|lavendel|lavender|veilchen|violet|blumig|floral|lilie|lily|pfingstrose|peony/,
      fresh: /frisch|fresh|clean|laundry|wasche|cotton|baumwolle|linen|leinen|breeze|brise|ozon|ocean|meer|aqua|spa|clarity/,
      woody: /holz|wood|sandel|sandal|zeder|cedar|amber|moschus|musk|patchouli|oud|tabak|tobacco|tonka|weihrauch|incense|gewurz|spice/,
      cozy: /cozy|cosy|pyjama|pajama|warm|cashmere|kaschmir|marsh|waffle|coffee|kaffee|brunch|vanille|vanilla|amber|tonka/,
    };

    Object.entries(familyTerms).forEach(([family, pattern]) => {
      if (pattern.test(signature)) families.add(family);
    });

    return families;
  }

  seasonalFamilies() {
    const month = new Date().getMonth() + 1;
    if (month === 12 || month <= 2) return new Set(['cozy', 'gourmand', 'woody']);
    if (month <= 5) return new Set(['floral', 'fresh', 'citrus']);
    if (month <= 8) return new Set(['fruity', 'citrus', 'fresh', 'tropical']);
    return new Set(['cozy', 'gourmand', 'woody']);
  }

  hashSignal(value) {
    return Array.from(String(value || '')).reduce((hash, character) => {
      return ((hash << 5) - hash + character.charCodeAt(0)) | 0;
    }, 0);
  }

  nextMilestoneGap(cart) {
    const thresholds = [4900, this.country === 'AT' ? 8900 : 6900, 9900];
    const nextThreshold = thresholds.find((threshold) => cart.total_price < threshold);
    return nextThreshold ? nextThreshold - cart.total_price : 0;
  }

  recommendationContext(cart) {
    const cartFamilies = new Set();
    cart.items.forEach((item) => {
      if (!this.isWaxItem(item)) return;
      this.inferScentFamilies(this.itemSignature(item)).forEach((family) => cartFamilies.add(family));
    });

    const cartKey = cart.items
      .map((item) => [item.product_id, item.variant_id, item.quantity].join(':'))
      .sort()
      .join('|');

    return {
      cartFamilies,
      cartKey,
      milestoneGap: this.nextMilestoneGap(cart),
      rotationWindow: Math.floor(Date.now() / 259200000),
      seasonalFamilies: this.seasonalFamilies(),
    };
  }

  familiesOverlap(first, second) {
    return Array.from(first).some((family) => second.has(family));
  }

  complementaryFamilyScore(candidateFamilies, cartFamilies) {
    const complements = {
      citrus: ['fruity', 'fresh', 'tropical'],
      fruity: ['citrus', 'fresh', 'floral', 'tropical'],
      tropical: ['citrus', 'fruity', 'fresh'],
      gourmand: ['cozy', 'woody'],
      floral: ['fresh', 'fruity'],
      fresh: ['citrus', 'fruity', 'floral', 'tropical'],
      woody: ['cozy', 'gourmand'],
      cozy: ['gourmand', 'woody'],
    };
    let score = 0;

    cartFamilies.forEach((family) => {
      (complements[family] || []).forEach((complement) => {
        if (candidateFamilies.has(complement)) score += 2;
      });
    });

    return Math.min(6, score);
  }

  scoreCandidate(card, context, selected) {
    const signature = card.dataset.recommendationSignature || '';
    const candidateFamilies = this.inferScentFamilies(signature);
    const bestsellerRank = Math.max(1, Number(card.dataset.bestsellerRank || 8));
    const price = Math.max(0, Number(card.dataset.priceCents || 0));
    let score = Math.max(2, 24 - ((bestsellerRank - 1) * 2.35));

    candidateFamilies.forEach((family) => {
      if (context.seasonalFamilies.has(family)) score += 2.25;
      if (context.cartFamilies.has(family)) score += 4.5;
    });
    score += this.complementaryFamilyScore(candidateFamilies, context.cartFamilies);

    if (context.milestoneGap > 0 && price > 0) {
      if (price >= context.milestoneGap) {
        score += Math.max(1, 7 - ((price - context.milestoneGap) / 500));
      } else {
        score += Math.min(3.5, (price / context.milestoneGap) * 3.5);
      }
    }

    if (card.dataset.recommendationKind === 'lamp') {
      const month = new Date().getMonth() + 1;
      const bright = /white|weiss|creme|gray|grau|glass|glas/.test(this.normalizeSignal(signature));
      const atmospheric = /noir|black|schwarz|smokey|bronze|midas|gold|mercury/.test(this.normalizeSignal(signature));
      if (bright && month >= 3 && month <= 8) score += 2.5;
      if (atmospheric && (month >= 9 || month <= 2)) score += 2.5;
    }

    if (selected.length > 0 && card.dataset.recommendationKind === 'scent') {
      const selectedFamilies = this.inferScentFamilies(selected[0].dataset.recommendationSignature || '');
      if (candidateFamilies.size > 0 && selectedFamilies.size > 0) {
        score += this.familiesOverlap(candidateFamilies, selectedFamilies) ? -3 : 2.5;
      }
    }

    const rotationSignal = [
      context.cartKey,
      context.rotationWindow,
      this.country,
      card.dataset.productId,
    ].join('|');
    score += (Math.abs(this.hashSignal(rotationSignal)) % 650) / 100;

    return score;
  }

  setRecommendationCopy(eyebrow, title, intro) {
    const eyebrowNode = this.querySelector('[data-md-upsell-eyebrow]');
    const titleNode = this.querySelector('[data-md-upsell-title]');
    const introNode = this.querySelector('[data-md-upsell-intro]');

    if (eyebrowNode) eyebrowNode.textContent = eyebrow;
    if (titleNode) titleNode.textContent = title;
    if (introNode) introNode.textContent = intro;
  }

  syncRecommendations(cart) {
    const productIds = new Set(cart.items.map((item) => String(item.product_id)));
    const candidates = Array.from(this.querySelectorAll('[data-md-recommendation]'));
    candidates.forEach((card) => {
      card.hidden = true;
      card.style.removeProperty('order');
    });

    const hasSensitiveDeliveryItem = cart.items.some((item) => {
      const signature = this.itemSignature(item);
      return Boolean(item.selling_plan_allocation) || /adventskalender|preorder|vorbestellung/.test(signature);
    });

    if (cart.item_count === 0 || hasSensitiveDeliveryItem) {
      this.upsellSection.hidden = true;
      return;
    }

    const hasHardware = cart.items.some((item) => this.isHardwareItem(item));
    const hasWax = cart.items.some((item) => this.isWaxItem(item));
    let recommendationPlan = ['scent', 'scent'];

    if (hasHardware && !hasWax) {
      this.setRecommendationCopy(
        'JETZT FEHLT NUR NOCH DEIN DUFT',
        'Mach deine Lampe duftbereit',
        'Zwei verfügbare Community-Favoriten für deinen ersten Mëlt-Moment.'
      );
    } else if (hasWax && !hasHardware) {
      recommendationPlan = ['lamp', 'scent'];
      this.setRecommendationCopy(
        'DEIN MËLT-MOMENT WIRD KOMPLETT',
        'Duft trifft Lieblingslampe',
        'Eine beliebte Duftlampe plus ein zweiter Duft, der deinen Warenkorb perfekt ergänzt.'
      );
    } else if (hasWax && hasHardware) {
      this.setRecommendationCopy(
        'PERFECT MATCH',
        'Noch ein Duft für deinen Mood?',
        'Zwei Community-Favoriten, die dein Duft-Ritual wunderbar ergänzen.'
      );
    } else {
      this.setRecommendationCopy(
        'VON MËLTIES GELIEBT',
        'Deine nächsten Favoriten',
        'Zwei verfügbare Bestseller, passend zu deinem Warenkorb.'
      );
    }

    const selected = [];
    const context = this.recommendationContext(cart);
    const pickCandidate = (kind) => candidates
      .filter((card) => {
        return !selected.includes(card) &&
          !productIds.has(card.dataset.productId) &&
          card.dataset.recommendationKind === kind;
      })
      .sort((first, second) => {
        const scoreDifference = this.scoreCandidate(second, context, selected) - this.scoreCandidate(first, context, selected);
        if (scoreDifference !== 0) return scoreDifference;
        return Number(first.dataset.bestsellerRank || 99) - Number(second.dataset.bestsellerRank || 99);
      })[0];

    recommendationPlan.forEach((kind) => {
      const card = pickCandidate(kind);
      if (card) selected.push(card);
    });

    while (selected.length < 2) {
      const fallback = pickCandidate('scent');
      if (!fallback) break;
      selected.push(fallback);
    }

    selected.forEach((card, index) => {
      card.style.order = String(index + 1);
      card.hidden = false;
    });
    this.upsellSection.hidden = selected.length === 0;
  }

  async changeLine(key, quantity) {
    if (!key) return;
    this.notification.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.cartChangeUrl + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error('Cart line could not be changed');
      this.renderCart(await response.json());
    } catch (error) {
      this.showError('Die Menge konnte gerade nicht geändert werden. Bitte versuche es erneut.');
    } finally {
      this.notification.removeAttribute('aria-busy');
    }
  }

  async addUpsell(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const label = button.querySelector('span');
    const originalLabel = label ? label.textContent : 'Hinzufügen';
    button.disabled = true;
    if (label) label.textContent = 'Wird hinzugefügt …';

    try {
      const response = await fetch(this.cartAddUrl + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error('Upsell could not be added');
      if (label) label.textContent = 'Hinzugefügt!';
      await this.refresh({ silent: true });
    } catch (error) {
      this.showError('Dieses Produkt konnte gerade nicht hinzugefügt werden.');
      if (label) label.textContent = originalLabel;
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        if (label && document.body.contains(button)) label.textContent = originalLabel;
      }, 900);
    }
  }

  showError(message) {
    const existing = this.querySelector('.md-cart-error');
    if (existing) existing.remove();

    const error = document.createElement('p');
    error.className = 'md-cart-error md-cart-calendar-note';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    this.itemsContainer.prepend(error);
  }

  updateHeaderCount(itemCount) {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;

    bubble.hidden = itemCount === 0;
    const visibleCount = bubble.querySelector('span[aria-hidden="true"]');
    const accessibleCount = bubble.querySelector('.visually-hidden');
    if (visibleCount) visibleCount.textContent = String(itemCount);
    if (accessibleCount) accessibleCount.textContent = itemCount + (itemCount === 1 ? ' Artikel' : ' Artikel');
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const cartIconSection = this.getSectionsToRender().find((section) => section.id === 'cart-icon-bubble');

    if (cartIconSection && parsedState.sections && parsedState.sections[cartIconSection.id]) {
      const target = document.getElementById(cartIconSection.id);
      const html = this.getSectionInnerHTML(parsedState.sections[cartIconSection.id], cartIconSection.selector);
      if (target && html !== null) target.innerHTML = html;
    }

    this.open();
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product', selector: '[id="cart-notification-product-' + this.cartItemKey + '"]' },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  formatMoney(cents, currency) {
    const locale = document.documentElement.lang || 'de-DE';
    const activeCurrency = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: activeCurrency }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return ((Number(cents) || 0) / 100).toFixed(2).replace('.', ',') + ' €';
    }
  }

  escapeHTML(value) {
    const element = document.createElement('div');
    element.textContent = value == null ? '' : String(value);
    return element.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHTML(value).replace(/`/g, '&#96;');
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
